import queryString from 'query-string';

import { endpoints } from '../config';
import { LAZY_DEFAULTS } from '../constants';
import createPolygonFromViewport from '../utils/createPolygonFromViewport';

const env = process.env.REACT_APP_API || 'local';

export function generateReportLink(options) {
  const { url, geoEndPoint } = endpoints[env];
  const base = `${url}${geoEndPoint}/report/location`;
  const urlParams = new URLSearchParams(options);

  // filter out invalid values for bufferRadii
  const { bufferRadii } = options;
  urlParams.set('bufferRadii', bufferRadii.filter(r => r));

  return `${base}?${urlParams.toString()}`;
}

export function generateAreaReportLink(type, options) {
  const { url, geoEndPoint } = endpoints[env];
  const base = `${url}${geoEndPoint}/report/${type}`;

  const query = `&${queryString.stringify(options, { encode: false })}`;
  return `${base}?${query}`;
}

export function getViewerCustomizations(org) {
  const { url, organizationEndPoint, viewerEndPoint } = endpoints[env];

  return fetch(`${url}${organizationEndPoint}/${org}${viewerEndPoint}`)
  .then(response => response.json());
}

export function getUserProfile() {
  const { url, userProfileEndPoint } = endpoints[env];

  return fetch(`${url}${userProfileEndPoint}`, { credentials: 'include' })
  .then(response => response.json());
}

export function logout() {
  const { url, userProfileEndPoint } = endpoints[env];

  return fetch(`${url}${userProfileEndPoint}`, {
    method: 'DELETE',
    credentials: 'include',
  });
}

export function getUserProfileUrl(key) {
  const { url, userProfileEndPoint } = endpoints[env];
  return `${url}${userProfileEndPoint}/${key}`;
}

export function getBusinessesByNaicsCode(polygon, naicsCode, viewport) {
  const { url, businessesEndPoint } = endpoints[env];
  const poly = [createPolygonFromViewport(viewport)];

  return fetch(`${url}${businessesEndPoint}?polygon=${JSON.stringify(poly)}&naicsCode=${naicsCode}`)
  .then(response => response.json());
}

export function getBuildings(org, parameters, maxResults = LAZY_DEFAULTS.INITIAL) {
  const { url, buildingsEndPoint } = endpoints[env];
  const max = maxResults ? `max_results=${maxResults}` : '';
  const find = parameters ? `${max ? '&' : ''}${queryString.stringify(parameters, { encode: false })}` : '';
  return fetch(`${url + buildingsEndPoint}/${org}?${max}${find}`)
  .then(response => response.json());
}

export function getBuilding(id, orgId) {
  const { url, buildingEndPoint } = endpoints[env];

  return fetch(`${url + buildingEndPoint}/${id}?organization=${orgId}`)
  .then(response => response.json());
}

export function getPropertyFilterValues(org, propertyType) {
  const { url } = endpoints[env];

  return fetch(`${url}/${propertyType}/${org}/filtervalues`)
  .then(response => response.json());
}

export function getPropertyPdfLink(propertyType, orgId, buildingId, withAttachments) {
  const { pdfUrl, url: apiUrl, propertyPdfEndPoint } = endpoints[env];

  if (withAttachments === true) {
    return `${apiUrl}/pdf/${propertyType}/${orgId}/${buildingId}?format=pdf`;
  }

  return `${pdfUrl + propertyPdfEndPoint}/${propertyType}/${orgId}/${buildingId}?format=pdf`;
}

export function getAreaPdfLink(area, layer, orgId) {
  const { pdfUrl, areaPdfEndPoint } = endpoints[env];

  const query = {
    id: area.id,
    _id: area._id,
    orgId,
  };

  if (layer === 'regions') {
    // We want to actually pass the county IDs
    query.id = area.counties.map(c => c.id).join(',');
  }

  return `${pdfUrl + areaPdfEndPoint}/${layer}?${queryString.stringify(query)}?format=pdf`;
}

export function getSites(org, parameters, maxResults = LAZY_DEFAULTS.INITIAL) {
  const { url, sitesEndPoint } = endpoints[env];
  const max = maxResults ? `max_results=${maxResults}` : '';
  const find = parameters ? `${max ? '&' : ''}${queryString.stringify(parameters, { encode: false })}` : '';

  return fetch(`${url + sitesEndPoint}/${org}?${max}${find}`)
  .then(response => response.json());
}

export function getSite(id, orgId) {
  const { url, siteEndPoint } = endpoints[env];

  return fetch(`${url + siteEndPoint}/${id}`, { organization: orgId })
  .then(response => response.json());
}

export function getDemographics(coordinates, radii, minutesFlag) {
  let radiusOne;
  let radiusTwo;
  let radiusThree;
  const [longitude, latitude] = coordinates;
  if (minutesFlag) {
    radiusOne = radii.minuteOne;
    radiusTwo = radii.minuteTwo;
    radiusThree = radii.minuteThree;
  } else {
    radiusOne = radii.radiusOne;
    radiusTwo = radii.radiusTwo;
    radiusThree = radii.radiusThree;
  }
  const { demographicsUrl, demographicsEndPoint } = endpoints[env];
  const url = `${demographicsUrl + demographicsEndPoint}?lat=${latitude}&lng=${longitude}&radiusOne=${radiusOne}&radiusTwo=${radiusTwo}&radiusThree=${radiusThree}&minutesFlag=${minutesFlag}`;
  return fetch(url)
  .then(response => {
    return response.json();
  });
}

export function getAreas(layer, org) {
  const { url, areasEndPoint } = endpoints[env];

  return fetch(`${url + areasEndPoint}/${layer}/list/${org}`)
  .then(response => response.json());
}

export function getAreaAll(org) {
  const { url, areasEndPoint } = endpoints[env];
  return fetch(`${url + areasEndPoint}/all/${org}`)
  .then(response => response.json());
}

export function getAreaData(geo, id, report) {
  const { url, areasEndPoint } = endpoints[env];

  return fetch(`${url + areasEndPoint}/report/${geo}/${id}/${report}`)
  .then(response => response.json());
}

export function getAreaPolygon(layer, ids, isProfile = false) {
  const { url, geoEndPoint } = endpoints[env];
  return fetch(
    `${url + geoEndPoint}/polygon/${layer}`,
    {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({ ids, isProfile }),
    },
  )
  .then(response => response.json());
}

export function getAreaPopulation(layer, ids) {
  const { url, areasEndPoint } = endpoints[env];
  const query = ids.map(id => `id=${id}`).join('&');

  return fetch(`${url + areasEndPoint}/population/${layer}?${query}`)
  .then(response => response.json());
}

export function getAreaList(org, type) {
  const { url, areasEndPoint } = endpoints[env];

  return fetch(`${url + areasEndPoint}/${type}/list/${org}`)
  .then(response => response.json());
}

export function getAreaDetails(layer, area) {
  const { url, areasEndPoint } = endpoints[env];

  return fetch(`${url + areasEndPoint}/details/${layer}/${area._id}`)
  .then(response => response.json());
}

export function exportBrochures({
  buildings,
  sites,
  states,
  regions,
  counties,
  communities,
  organizationId,
}) {
  const { pdfUrl, exportFavoritesPdfEndpoint } = endpoints[env];

  const qs = [
    { key: 'buildings', value: buildings.map(b => b._id) },
    { key: 'sites', value: sites.map(s => s._id) },
    { key: 'states', value: states.map(s => ({ id: s.id, _id: s._id })) },
    { key: 'regions', value: regions.map(r => ({ id: r.id, _id: r._id })) },
    { key: 'counties', value: counties.map(c => ({ id: c.id, _id: c._id })) },
    { key: 'communities', value: communities.map(c => ({ id: c.id, _id: c._id })) },
  ].map(({ key, value }) => `${key}=${encodeURIComponent(JSON.stringify(value))}`).join('&');

  return `${pdfUrl + exportFavoritesPdfEndpoint}/${organizationId}?${qs}`;
}

export function exportList({
  buildings,
  sites,
  organizationId,
  isTour,
}) {
  const { pdfUrl, favoritesListPdfEndpoint } = endpoints[env];

  const qs = [
    { key: 'buildings', value: buildings.map(b => b._id) },
    { key: 'sites', value: sites.map(s => s._id) },
    { key: 'isTour', value: isTour },
  ].map(({ key, value }) => `${key}=${encodeURI(JSON.stringify(value))}`).join('&');

  return `${pdfUrl + favoritesListPdfEndpoint}/${organizationId}?${qs}`;
}

// eslint-disable-next-line
export function exportAreas(type, { states, regions, counties, communities }) {
  const { url, geoEndPoint } = endpoints[env];

  const qs = [
    { key: 'states', value: states.map(s => ({ name: s.name, id: s.id })) },
    { key: 'regions', value: regions.map(r => ({ name: r.name, id: r.counties.map(c => c.id) })) },
    { key: 'counties', value: counties.map(c => ({ name: c.name, id: c.id })) },
    { key: 'communities', value: communities.map(c => ({ name: c.name, id: c.id })) },
  ].map(({ key, value }) => `${key}=${encodeURIComponent(JSON.stringify(value))}`).join('&');

  return `${url + geoEndPoint}/batch-report/${type}?${qs}`;
}

export function exportExcel({
  buildings,
  sites,
  organizationId,
}) {
  const { url, favoritesEndPoint } = endpoints[env];

  const qs = [
    { key: 'buildings', value: buildings.map(b => b._id) },
    { key: 'sites', value: sites.map(s => s._id) },
  ].map(({ key, value }) => `${key}=${encodeURI(JSON.stringify(value))}`).join('&');

  return `${url}${favoritesEndPoint}/export/excel/${organizationId}?${qs}`;
}

export function exportRFI({
  projectTitle,
  buildings,
  sites,
  organizationId,
}) {
  const { url, favoritesEndPoint } = endpoints[env];

  const qs = [
    { key: 'projectTitle', value: projectTitle },
    { key: 'buildings', value: buildings.map(b => b._id) },
    { key: 'sites', value: sites.map(s => s._id) },
  ].map(({ key, value }) => `${key}=${encodeURI(JSON.stringify(value))}`).join('&');

  return `${url}${favoritesEndPoint}/export/rfi/${organizationId}?${qs}`;
}

export function getOauthUrl(provider) {
  const { url, oAuthEndPoint } = endpoints[env];

  return `${url}${oAuthEndPoint}/${provider}`;
}

// fetch a token directly from ArcGIS as shown in the README
export function getArcGisToken() {
  return fetch('https://www.arcgis.com/sharing/oauth2/token?client_id=cyCf3yGQo2JX9atx&grant_type=client_credentials&client_secret=f999ab4c6aa44c7fb776e7952a33d6b4&f=pjson&expiration=480')
  .then(response => response.json());
}



// WEBPACK FOOTER //
// ./src/ajax/endpoints.js